import { clamp } from '@/utils/math-util'

/**
 * Red/green/blue color, each channel 0-255.
 */
export interface Rgb {
  /**
   * Red channel.
   */
  r: number
  /**
   * Green channel.
   */
  g: number
  /**
   * Blue channel.
   */
  b: number
}

/**
 * Converts a hex color to RGB.
 *
 * @param hex
 */
export function hexToRgb(hex: string): Rgb {
  hex = hex.replace(/^#/, '')

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('')
  }

  const int = parseInt(hex, 16)
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 }
}

/**
 * Converts RGB to hex.
 *
 * @param rgb
 */
export function rgbToHex(rgb: Rgb): string {
  const channel = (value: number) =>
    Math.round(clamp(value, 0, 255))
      .toString(16)
      .padStart(2, '0')

  return `#${channel(rgb.r)}${channel(rgb.g)}${channel(rgb.b)}`
}

/**
 * Calculates the relative luminance of a color according to WCAG 2.
 * Returns a value between 0 (darkest) and 1 (lightest).
 *
 * @param color
 */
export function getLuminance(color: string | Rgb): number {
  const { r, g, b } = typeof color === 'string' ? hexToRgb(color) : color
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

/**
 * The WCAG 2 contrast ratio between two colors, from 1 to 21.
 *
 * @param a
 * @param b
 */
export function getContrastRatio(a: string | Rgb, b: string | Rgb): number {
  const [lighter, darker] = [getLuminance(a), getLuminance(b)].sort(
    (x, y) => y - x,
  )
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * The APCA (0.0.98G) lightness contrast of text on a background, as an
 * absolute Lc value from 0 to about 108.
 *
 * WCAG 2's ratio overstates how dark a background must be for light text,
 * so it is used where light text sits on a brand color.
 *
 * @param text
 * @param background
 */
export function getApcaContrast(
  text: string | Rgb,
  background: string | Rgb,
): number {
  const txt = softClampBlack(getApcaLuminance(text))
  const bg = softClampBlack(getApcaLuminance(background))

  // Below this difference the two are indistinguishable.
  if (Math.abs(bg - txt) < 0.0005) {
    return 0
  }

  // Dark text on a light background and light on dark use different exponents.
  const sapc =
    bg > txt
      ? (bg ** 0.56 - txt ** 0.57) * 1.14
      : (bg ** 0.65 - txt ** 0.62) * 1.14

  if (Math.abs(sapc) < 0.1) {
    return 0
  }
  return (Math.abs(sapc) - 0.027) * 100
}

/**
 * Darkens a color just until `passes` accepts it, by lowering its OKLCH
 * lightness. Unlike mixing in black, this keeps the hue, so the result
 * still reads as the same color. A color that already passes is returned
 * unchanged.
 *
 * @param color
 * @param passes
 */
export function darkenUntil(
  color: string,
  passes: (candidate: string) => boolean,
): string {
  if (passes(color)) {
    return color.toLowerCase()
  }

  const start = rgbToOklch(hexToRgb(color))
  let candidate = color
  for (let l = start.l - lightnessStep; l > 0; l -= lightnessStep) {
    candidate = rgbToHex(oklchToRgb({ ...start, l }))
    if (passes(candidate)) {
      return candidate
    }
  }
  return candidate
}

interface Oklch {
  l: number
  c: number
  h: number
}

const lightnessStep = 0.005

function toLinear(channel: number) {
  const value = channel / 255
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

function fromLinear(value: number) {
  return (
    255 *
    (value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055)
  )
}

function getApcaLuminance(color: string | Rgb) {
  const { r, g, b } = typeof color === 'string' ? hexToRgb(color) : color
  return (
    0.2126729 * (r / 255) ** 2.4 +
    0.7151522 * (g / 255) ** 2.4 +
    0.072175 * (b / 255) ** 2.4
  )
}

function softClampBlack(luminance: number) {
  return luminance > 0.022
    ? luminance
    : luminance + (0.022 - luminance) ** 1.414
}

// The OKLab matrices are from https://bottosson.github.io/posts/oklab/.
function rgbToOklch({ r, g, b }: Rgb): Oklch {
  const [lr, lg, lb] = [toLinear(r), toLinear(g), toLinear(b)]
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)

  const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  return { l: okL, c: Math.hypot(okA, okB), h: Math.atan2(okB, okA) }
}

function oklchToLinearRgb({ l, c, h }: Oklch): [number, number, number] {
  const a = c * Math.cos(h)
  const b = c * Math.sin(h)
  const lc = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const mc = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const sc = (l - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc,
    -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc,
    -0.0041960863 * lc - 0.7034186147 * mc + 1.707614701 * sc,
  ]
}

function oklchToRgb(color: Oklch): Rgb {
  // Darker shades hold less chroma in sRGB, so an out-of-gamut result sheds
  // chroma, keeping its lightness and hue, until it fits.
  let linear = oklchToLinearRgb(color)
  for (let c = color.c; c > 0 && !inGamut(linear); c -= 0.002) {
    linear = oklchToLinearRgb({ ...color, c })
  }
  const [r, g, b] = linear.map((value) => fromLinear(clamp(value)))
  return { r, g, b }
}

function inGamut(linear: number[]) {
  return linear.every((value) => value >= -1e-4 && value <= 1 + 1e-4)
}
